import { Capacitor, registerPlugin } from "@capacitor/core";
import { App } from "@capacitor/app";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";

// True inside the iOS/Android app built with Capacitor; false on the website.
export const isNativeApp = Capacitor.isNativePlatform();

// Fired (cancelable) on window when the Android back button is pressed, before
// anything else happens. A screen with its own overlay that isn't a Radix
// component (e.g. the login prompt) closes it and calls preventDefault().
export const NATIVE_BACK_EVENT = "inspirelens:nativeBack";
// Fired when Back is pressed on a screen with nothing to go back to that isn't
// Home (e.g. after a code-driven navigation Chromium skips in history).
// AppContent navigates to "/" instead of the app exiting.
export const NATIVE_GO_HOME_EVENT = "inspirelens:nativeGoHome";

// Radix dialogs, sheets, alert dialogs, menus, selects and popovers. All of
// them close on Escape.
const OPEN_OVERLAY_SELECTOR = [
  '[role="dialog"][data-state="open"]',
  '[role="alertdialog"][data-state="open"]',
  '[role="menu"][data-state="open"]',
  '[role="listbox"][data-state="open"]',
  '[data-radix-popper-content-wrapper] [data-state="open"]',
].join(", ");

// App-local native plugin (android/.../AppChromePlugin.java).
const AppChrome = registerPlugin<{
  setNavigationBarColor(options: { color: string; darkIcons: boolean }): Promise<void>;
  isPushConfigured(): Promise<{ configured: boolean }>;
}>("AppChrome");

// Whether push notifications can be set up in this build. Android needs
// Firebase (google-services.json); without it the push plugin's register()
// fails natively. iOS registers with APNs either way (lib/push.ts ignores the
// token until Firebase Messaging is added). Always false on the website.
export async function isPushConfigured(): Promise<boolean> {
  if (Capacitor.getPlatform() === "ios") return true;
  if (Capacitor.getPlatform() !== "android") return false;
  try {
    return (await AppChrome.isPushConfigured()).configured;
  } catch {
    return false;
  }
}

let initialized = false;

// One-time setup for the native shell: status bar colors, hiding the splash
// screen once React has rendered, and the Android hardware back button
// (close the open overlay, else history back, else Home, else leave the app).
export function initNativeShell(): void {
  if (!isNativeApp || initialized) return;
  initialized = true;

  document.documentElement.classList.add("native-app");

  StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
  if (Capacitor.getPlatform() === "android") {
    StatusBar.setBackgroundColor({ color: "#14213d" }).catch(() => {});
    syncNavigationBarWithTheme();
  }

  App.addListener("backButton", ({ canGoBack }) => {
    if (!window.dispatchEvent(new Event(NATIVE_BACK_EVENT, { cancelable: true }))) return;

    if (document.querySelector(OPEN_OVERLAY_SELECTOR)) {
      // Radix listens for Escape on the document (capture phase).
      (document.activeElement ?? document).dispatchEvent(
        new KeyboardEvent("keydown", { key: "Escape", bubbles: true, cancelable: true })
      );
      return;
    }

    if (canGoBack) {
      window.history.back();
    } else if (window.location.pathname !== "/") {
      window.dispatchEvent(new Event(NATIVE_GO_HOME_EVENT));
    } else {
      App.exitApp();
    }
  });

  SplashScreen.hide().catch(() => {});
}

// Runs `callback` when the app returns to the foreground. Returns a cleanup
// function; a no-op on the website.
export function onAppResume(callback: () => void): () => void {
  if (!isNativeApp) return () => {};
  const handle = App.addListener("resume", callback);
  return () => {
    handle.then((h) => h.remove()).catch(() => {});
  };
}

// Paints the strip behind the Android navigation bar in the tab bar's colour
// (white, or slate-900 in dark mode) and picks contrasting gesture-bar icons.
// Only visible on WebViews older than 140; newer ones draw the page under it.
function syncNavigationBarWithTheme(): void {
  const root = document.documentElement;
  let lastDark: boolean | null = null;
  const apply = () => {
    const dark = root.classList.contains("dark");
    if (dark === lastDark) return;
    lastDark = dark;
    AppChrome.setNavigationBarColor({ color: dark ? "#0f172a" : "#ffffff", darkIcons: !dark }).catch(() => {});
  };
  apply();
  new MutationObserver(apply).observe(root, { attributes: true, attributeFilter: ["class"] });
}
